import { TooltipComponent } from "../../components/Component";
import React from "react";
import { useFormContext } from "react-hook-form";
import { FieldErrors, FieldValues } from "react-hook-form";

interface FormInputProps {
  name: string;
  label: any;
  optionLabel?: any;
  optionLabelWidth?: any;
  unMaskView?: boolean;
  type?: string;
  required?: boolean;
  hideRequiredMark?: boolean;
  minLength?: number;
  maxLength?: number;
  placeholder?: string;
  column?: string;
  pattern?: string | RegExp;
  patternErrorMessage?: string;
  customValidation?: { validate: (value: string) => boolean | string };
  defaultValue?: string | number;
  disabled?: boolean;
  tooltipText?: string;
  min?: any;
  max?: any;
  isPercentage?: boolean;
  noMaxLength?: boolean;
  onKeyDown?: any;
  onInput?: (event: React.FormEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  accept?: string;
  value?: string | number;
  rows?: number;
  autoFocus?: boolean;
}

const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  type = "text",
  optionLabel,
  optionLabelWidth = "34px",
  unMaskView = false,
  required = false,
  hideRequiredMark = false,
  minLength,
  maxLength,
  placeholder,
  column,
  pattern,
  patternErrorMessage,
  customValidation,
  defaultValue,
  disabled,
  tooltipText,
  min,
  max,
  isPercentage = false,
  noMaxLength = false,
  onKeyDown,
  onInput,
  onChange,
  onBlur,
  accept = ".doc, .docx, .png, .pdf, .jpeg, .jpg, .gif",
  value,
  rows,
  autoFocus,
}) => {
  const {
    register,
    formState: { errors },
    trigger,
  } = useFormContext();
  const errorMessage = getErrorMessage(errors, name);

  const EMOJI_REGEX =
    /(?:[\u2700-\u27BF]|(?:\uD83C[\uDDE6-\uDDFF]){2}|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\u0023-\u0039]\uFE0F?\u20E3|\u3299|\u3297|\u303D|\u3030|\u24C2|\uD83C[\uDD70-\uDD71]|\uD83C[\uDD7E-\uDD7F]|\uD83C\uDD8E|\uD83C[\uDD91-\uDD9A]|\uD83C[\uDDE6-\uDDFF]|[\uD83C\uDE01-\uDE02]|\uD83C\uDE1A|\uD83C\uDE2F|[\uD83C\uDE32-\uDE3A]|[\uD83C\uDE50-\uDE51]|\u203C|\u2049|[\u25AA-\u25AB]|\u25B6|\u25C0|[\u25FB-\u25FE]|\u00A9|\u00AE|\u2122|\u2139|\uD83C\uDC04|[\u2600-\u26FF]|\u2B05|\u2B06|\u2B07|\u2B1B|\u2B1C|\u2B50|\u2B55|\u231A|\u231B|\u2328|\u23CF|[\u23E9-\u23F3]|[\u23F8-\u23FA]|\uD83C\uDCCF|\u2934|\u2935|[\u2190-\u21FF]|\uFE0F|\u200D|[\uD83C-\uDBFF\uDC00-\uDFFF])/g;

  const stripEmojis = (value: string) => value.replace(EMOJI_REGEX, "");

  const PERCENTAGE_PATTERN = /^(100(\.0{1,2})?|\d{1,2}(\.\d{1,2})?)$/;
  const PERCENTAGE_ERROR =
    "Enter a valid percentage between 0 and 100 (up to 2 decimal places)";

  const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  const HSN_PATTERN = /^$|^[0-9]{4,8}$/;
  const GSTIN_ERROR = "Invalid GSTIN format (e.g. 22ABCDE1234F1Z5)";
  const PAN_ERROR = "Invalid PAN format (e.g. ABCDE1234F)";
  const HSN_ERROR = "HSN Code must be 4-8 digits";

  const fieldKey = name?.toLowerCase();
  const isGstinField = fieldKey === "gstin";
  const isPanField = fieldKey === "pan";
  const isHsnField = fieldKey === "hsncode";

  const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/;

  const effectiveType = isPercentage ? "number" : type;
  const effectiveMin = isPercentage ? 0 : min;
  const effectiveMax = isPercentage ? 100 : max;
  const effectiveStep = isPercentage ? "0.01" : type === "number" ? "any" : undefined;

  const DEFAULT_MAX_LENGTH = 100;
  const skipDefaultMaxLength =
    isPercentage || type === "number" || type === "file" || noMaxLength;
  const effectiveMaxLength =
    maxLength ??
    (isGstinField
      ? 15
      : isPanField
      ? 10
      : skipDefaultMaxLength
      ? undefined
      : DEFAULT_MAX_LENGTH);

  const effectivePattern =
    pattern ??
    (isPercentage
      ? PERCENTAGE_PATTERN
      : type === "email"
      ? EMAIL_PATTERN
      : isGstinField
      ? GSTIN_PATTERN
      : isPanField
      ? PAN_PATTERN
      : isHsnField
      ? HSN_PATTERN
      : undefined);
  const effectivePatternError =
    patternErrorMessage ??
    (isPercentage
      ? PERCENTAGE_ERROR
      : type === "email"
      ? "Please enter a valid email address"
      : isGstinField
      ? GSTIN_ERROR
      : isPanField
      ? PAN_ERROR
      : isHsnField
      ? HSN_ERROR
      : undefined);

  const resolvedPattern =
    typeof effectivePattern === "string"
      ? new RegExp(effectivePattern)
      : effectivePattern;

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    let cleaned = stripEmojis(target.value).replace(/^\s+/, "");
    if (isPercentage) {
      cleaned = cleaned.replace(/[^0-9.]/g, "");
      const firstDot = cleaned.indexOf(".");
      if (firstDot !== -1) {
        cleaned =
          cleaned.slice(0, firstDot + 1) +
          cleaned.slice(firstDot + 1).replace(/\./g, "");
        const [intPart, decPart = ""] = cleaned.split(".");
        cleaned = intPart + "." + decPart.slice(0, 2);
      }
      const asNum = parseFloat(cleaned);
      if (!isNaN(asNum) && asNum > 100) {
        cleaned = "100";
      }
    }
    if (isGstinField || isPanField) {
      cleaned = cleaned.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    }
    if (isHsnField) {
      cleaned = cleaned.replace(/[^0-9]/g, "").slice(0, 8);
    }
    if (cleaned !== target.value) {
      target.value = cleaned;
    }
    if (onInput) {
      onInput(event);
    }
    if (resolvedPattern || customValidation || isPercentage || type === "email") {
      queueMicrotask(() => trigger(name));
    }
  };

  const registerProps = {
    ...register(name, {
      required: required ? "This field is required" : undefined,
      minLength: minLength
        ? {
            value: minLength,
            message: `Must be at least ${minLength} characters`,
          }
        : undefined,
      maxLength: effectiveMaxLength
        ? {
            value: effectiveMaxLength,
            message: `Cannot be more than ${effectiveMaxLength} characters`,
          }
        : undefined,
      pattern: resolvedPattern
        ? {
            value: resolvedPattern,
            message: effectivePatternError || "Invalid format",
          }
        : undefined,

      validate: (value: any) => {
        if (
          required &&
          (value == null || String(value).trim() === "")
        ) {
          return "This field is required";
        }
        if (customValidation) return customValidation.validate(value);
        return true;
      },
    }),
  };

  return (
    <div className={column}>
      <div
        className={`form-group ${optionLabel ? "mb-1" : ""} ${
          unMaskView ? "unmask-view" : ""
        }`}
      >
        {label && (
          <div className="form-label-wrapper">
            <label className="form-label" htmlFor={name}>
              {label}
              {required && !hideRequiredMark && (
                <span className="mandatory">*</span>
              )}
            </label>
            {tooltipText && name && (
              <TooltipComponent
                iconClass="card-hint"
                icon="help-fill"
                direction="top"
                id={`${name}-tooltip`}
                text={tooltipText}
                containerClassName={""}
              />
            )}
          </div>
        )}
        <div className="form-control-wrap">
          <div
            className={`form-control-select ${
              optionLabel ? "d-flex align-items-center" : ""
            }`}
          >
            {optionLabel && (
              <label
                className={`form-label`}
                style={{ minWidth: optionLabelWidth }}
                htmlFor={name}
              >
                {optionLabel}
              </label>
            )}
            {type === "textarea" ? (
              <textarea
                id={name}
                placeholder={placeholder}
                className="form-control"
                rows={rows}
                {...register(name, {
                  required: required ? "This field is required" : undefined,
                  minLength: minLength
                    ? {
                        value: minLength,
                        message: `Must be at least ${minLength} characters`,
                      }
                    : undefined,
                  maxLength: effectiveMaxLength
                    ? {
                        value: effectiveMaxLength,
                        message: `Cannot be more than ${effectiveMaxLength} characters`,
                      }
                    : undefined,
                  pattern: resolvedPattern
                    ? {
                        value: resolvedPattern,
                        message: effectivePatternError || "Invalid format",
                      }
                    : undefined,

                  validate: (value: any) => {
                    if (
                      required &&
                      (value == null || String(value).trim() === "")
                    ) {
                      return "This field is required";
                    }
                    if (customValidation)
                      return customValidation.validate(value);
                    return true;
                  },
                })}
                onInput={(e) => {
                  const target = e.currentTarget;
                  const cleaned = stripEmojis(target.value).replace(
                    /^\s+/,
                    ""
                  );
                  if (cleaned !== target.value) {
                    target.value = cleaned;
                  }
                  if (resolvedPattern || customValidation) {
                    queueMicrotask(() => trigger(name));
                  }
                }}
                defaultValue={defaultValue}
                disabled={disabled}
                maxLength={effectiveMaxLength}
                autoFocus={autoFocus}
              />
            ) : (
              <input
                type={effectiveType}
                id={name}
                placeholder={placeholder}
                className="form-control"
                {...register(name, {
                  required: required ? "This field is required" : undefined,
                  minLength: minLength
                    ? {
                        value: minLength,
                        message: `Must be at least ${minLength} characters`,
                      }
                    : undefined,
                  maxLength: effectiveMaxLength
                    ? {
                        value: effectiveMaxLength,
                        message: `Cannot be more than ${effectiveMaxLength} characters`,
                      }
                    : undefined,
                  pattern: resolvedPattern
                    ? {
                        value: resolvedPattern,
                        message: effectivePatternError || "Invalid format",
                      }
                    : undefined,

                  validate: (value: any) => {
                    if (
                      required &&
                      (value == null || String(value).trim() === "")
                    ) {
                      return "This field is required";
                    }
                    if (customValidation)
                      return customValidation.validate(value);
                    return true;
                  },
                })}
                onInput={handleInput}
                onChange={(e) => {
                  registerProps.onChange(e);
                  if (onChange) {
                    onChange(e);
                  }
                }}
                onBlur={(e) => {
                  registerProps.onBlur(e);
                  if (onBlur) {
                    onBlur(e);
                  }
                }}
                defaultValue={defaultValue}
                min={effectiveMin}
                max={effectiveMax}
                step={effectiveStep}
                maxLength={effectiveMaxLength}
                onKeyDown={onKeyDown}
                autoComplete="off"
                disabled={disabled}
                accept={type === "file" ? accept : undefined}
                autoFocus={autoFocus}
              />
            )}
          </div>
          {errorMessage && !optionLabel && (
            <p className="mandatory">{errorMessage}</p>
          )}
        </div>
        {errorMessage && optionLabel && (
          <p className="mandatory">{errorMessage}</p>
        )}
      </div>
    </div>
  );
};

function getErrorMessage<T extends FieldValues>(
  errors: FieldErrors<T>,
  fieldName: string
): string | undefined {
  const parts = fieldName.split(/[\[\].]+/).filter(Boolean);
  let currentError: any = errors;

  for (const part of parts) {
    if (currentError && typeof currentError === "object") {
      currentError = currentError[part];
    } else {
      return undefined;
    }
  }

  if (
    currentError &&
    "message" in currentError &&
    typeof currentError.message === "string"
  ) {
    return currentError.message;
  }

  return undefined;
}

export default FormInput;
